/*

? Uploading chat attachments from the website (chat audit F3 / I13, 17 Oct 2026): photos, PDFs
? and voice notes, through the same upload steps as every other file (lib/uploadFiles.ts).

  A chat file is tagged as one (`use_case` chat_image / chat_file / chat_audio, the chat as its
  entity), carries its real mime type, and belongs to the person sending it. A business id goes up
  only while acting as a business; otherwise the file is the person's alone (a personal sender has
  no business id, and `new ObjectId("")` used to crash the photo send). send-message then checks
  every file (lib/chat/validateChatAttachments.ts) and links it to the conversation.

  Answers the uploaded files as `{ file_id, mime_type }`, in the order given, or null when any of
  them failed (a request error, or an upload link that ran out before the file went up).

*/

import { ObjectId } from "bson";
import uploadFiles from "./uploadFiles";

/** What the chat upload helper needs to know about each file. */
export type ChatUploadFile = {
	file: File;
	mime_type: string;
	/** A voice note's length in seconds, stored with the file. */
	duration?: number;
};

export type ChatUseCase = "chat_image" | "chat_file" | "chat_audio";

export async function uploadChatFiles(args: {
	session: any;
	profile_context: any;
	chatId: string;
	files: Array<ChatUploadFile>;
	useCase: ChatUseCase;
}): Promise<Array<{ file_id: string; mime_type: string }> | null> {
	const { session, profile_context, chatId, files, useCase } = args;
	if (files.length === 0) return [];

	const viewerUserId: string = session?.user?.dbUser?._id ? String(session.user.dbUser._id) : "";
	const uploaderId: string = ObjectId.isValid(String(profile_context?.user_id || ""))
		? String(profile_context.user_id)
		: viewerUserId;
	const actingAsBusiness: boolean =
		profile_context?.is_business_profile === true &&
		ObjectId.isValid(String(profile_context?.business_profile_id || ""));

	let lastStages: Array<string> = [];
	try {
		const result = await uploadFiles(
			session ? (session.authorized ? (session.session_id ? session.session_id : "") : "") : "",
			files.map((item: ChatUploadFile) => ({
				file: item.file,
				mime_type: item.mime_type,
				original_file_id: new ObjectId(),
				original_file_server_path: "",
				original: true,
				is_business_profile: actingAsBusiness,
				user_id: ObjectId.isValid(uploaderId) ? new ObjectId(uploaderId) : new ObjectId(),
				// Only while acting as a business; otherwise the file is the user's alone.
				business_profile_id: actingAsBusiness ? new ObjectId(String(profile_context.business_profile_id)) : "",
				entity: "business_profile",
				entity_id: chatId && ObjectId.isValid(String(chatId)) ? new ObjectId(String(chatId)) : new ObjectId(),
				use_case: useCase,
				uiPath: "/",
				isPublic: false,
				text: useCase,
				...(typeof item.duration === "number" && item.duration > 0 ? { duration: item.duration } : {}),
			})),
			(uploadProgress: Array<{ stage: string }>) => {
				lastStages = uploadProgress.map((progress) => progress.stage);
			},
		);
		const uploaded: Array<any> = result && result.error === false ? result.uploadedFiles || [] : [];
		// A file whose upload link had run out was never stored, though the helper carries on.
		if (lastStages.some((stage) => stage === "PRESIGNED_URL_EXPIRED")) return null;
		if (uploaded.length !== files.length || uploaded.some((file) => !file || !file._id)) return null;
		return uploaded.map((file: any, index: number) => ({
			file_id: String(file._id),
			mime_type: files[index].mime_type || file.mime_type || "",
		}));
	} catch {
		return null;
	}
}
